import { alpha, keyframes } from "@mui/material/styles"
import { makeStyles } from "tss-react/mui"

import { BUFFER_SIZE_ATTR } from "./helpers"

// Same keyframes as MUI `<Skeleton animation="wave" />` (emotion name is injected into `animation`).
const laneSkeletonWaveKeyframes = keyframes({
    "0%": { transform: "translateX(-100%)" },
    "50%": { transform: "translateX(100%)" },
    "100%": { transform: "translateX(100%)" },
})

type StyleProps = Readonly<{
    isDayGrid: boolean
    isEmptyResources: boolean
    emptyResourcesText: string
}>

export const useCalendarStyles = makeStyles<StyleProps>()(
    (theme, { isDayGrid, isEmptyResources, emptyResourcesText }) => ({
        container: {
            width: "100%",
            margin: "0 auto",
            flexGrow: 1,
            display: "flex",
            height: "calc(100% - 70px)",

            "& .fc .fc-scrollgrid-section-body table, & .fc .fc-scrollgrid-section table": {
                borderBottomStyle: "none",
                width: "100%!important",
            },

            "& .fc .fc-toolbar-chunk .fc-button.fc-button-primary": {
                border: `1px solid ${theme.palette.tailwind.slate[300]}`,
                borderRadius: ".625rem",
            },
            "& .fc .fc-toolbar-chunk .fc-button-group": {
                gap: ".25rem",
            },

            "& .fc": {
                flexGrow: 1,

                "& .fc-toolbar-title": {
                    fontSize: "1rem",
                },
                "& .fc-timeline-header-row-chrono": {
                    "& .fc-timeline-slot-cushion": {
                        width: "100%",
                        textAlign: "center",
                        fontWeight: "normal",
                    },
                },
                "& .fc-view-harness": {
                    overflow: "hidden",
                },
                "& .fc-scrollgrid-section-sticky > *": {
                    background: "transparent",
                },
                "& .fc-datagrid-body.fc-scrollgrid-sync-table": {
                    position: "relative",
                    "&:before": isEmptyResources
                        ? {
                              content: `"${emptyResourcesText}"`,
                              display: "block",
                              padding: "1rem",
                          }
                        : {},
                },
                "& .fc-timeline-overlap-enabled .fc-timeline-lane-frame .fc-timeline-events": {
                    paddingBottom: "0",
                },
                "& .fc-timeline-lane-frame": {
                    minHeight: "1.75rem",
                },
                "& .fc-toolbar.fc-header-toolbar": {
                    padding: ".75rem 1rem",
                    margin: "0",
                    gap: "1rem",
                    flexWrap: "wrap",
                    [theme.breakpoints.down("sm")]: {
                        padding: ".5rem",
                        gap: ".5rem",
                    },
                },
                "& .fc-header-toolbar .fc-toolbar-chunk:first-child": {
                    flex: "1 1 auto",
                    minWidth: 0,
                },
                "& .fc-resource-timeline .fc-resource-group:not([rowspan])": {
                    background: theme.palette.tailwind.slate[100],
                },
                "& .fc-button .fc-icon": {
                    fontSize: "1rem",
                },
                "& .fc-button:disabled": {
                    opacity: ".75",
                },
                "& .fc-button-group > .fc-goToDate-button": {
                    marginLeft: ".75rem",
                },
                "& .fc-button-primary, & .fc-button-primary:not(:disabled)": {
                    backgroundColor: "transparent",
                    color: theme.palette.tailwind.slate[950],
                    fontSize: ".9rem",
                    fontWeight: "500",
                    border: "none",
                },
                "& .fc-resourceTimelineWeek-button, & .fc-resourceTimelineDay-button": {
                    marginRight: "1rem",
                },
                "& .fc-button-primary:focus, & .fc-button-primary:not(:disabled).fc-button-active:focus, & .fc-button-primary:not(:disabled):active:focus":
                    {
                        boxShadow: "none",
                    },
            },

            "& .fc-timeline-event-harness": isDayGrid
                ? {
                      height: "100% !important",
                      top: "0 !important",
                  }
                : {},
            "& .fc-timeline-events": isDayGrid
                ? {
                      height: "100% !important",
                  }
                : {},

            "& .custom-event": {
                height: "100%",
                borderRadius: ".5rem",
                borderTopRightRadius: isDayGrid ? "0" : ".5rem",
                borderBottomRightRadius: isDayGrid ? "0" : ".5rem",
                border: "none",
                cursor: "pointer",
                padding: isDayGrid ? "0 .75rem" : ".5rem .75rem",
                margin: isDayGrid ? "0" : "0 0.125rem 0.25rem 0.125rem",

                [`&[style*="${BUFFER_SIZE_ATTR}: 0rem"]`]: {
                    borderTopRightRadius: ".5rem",
                    borderBottomRightRadius: ".5rem",
                },

                "&.custom-event--short": {
                    padding: isDayGrid ? "0" : ".5rem .75rem",
                },

                "&:after": {
                    content: '""',
                    position: "absolute",
                    top: "0",
                    right: "0",
                    bottom: "0",
                    width: `var(${BUFFER_SIZE_ATTR}, .625rem)`,
                    transform: "translateX(100%)",
                    background: theme.palette.tailwind.slate[300],
                    height: "100%",
                    borderTopRightRadius: ".5rem",
                    borderBottomRightRadius: ".5rem",
                    display: isDayGrid ? "block" : "none",
                },

                "&.fc-h-event .fc-event-main": {
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    color: theme.palette.tailwind.slate[900],
                    fontWeight: "500",
                },
            },

            // Week view: visually disable previous days and prevent interaction
            ...(isDayGrid
                ? {}
                : {
                      "& .fc td.fc-timeline-slot.fc-past, & .fc th.fc-timeline-slot.fc-past": {
                          backgroundColor: theme.palette.tailwind.slate[50],
                      },
                      "& .fc td.fc-timeline-lane.fc-past": {
                          backgroundColor: theme.palette.tailwind.slate[50],
                      },
                      "& .custom-event.fc-event-past, & .custom-event.fc-past": {
                          backgroundColor: `${theme.palette.tailwind.slate[200]} !important`,
                          cursor: "default",
                          pointerEvents: "none",
                          opacity: 0.85,
                      },
                      "& .custom-event.custom-event--past": {
                          backgroundColor: `${theme.palette.tailwind.slate[200]} !important`,
                          cursor: "default",
                          pointerEvents: "none",
                          opacity: 0.85,
                      },
                      "& .custom-event.fc-event-past .fc-event-main, & .custom-event.fc-past .fc-event-main": {
                          color: theme.palette.tailwind.slate[700],
                      },
                      "& .custom-event.custom-event--past .fc-event-main": {
                          color: theme.palette.tailwind.slate[700],
                      },
                  }),
        },
        resourceLabel: {
            cursor: isDayGrid ? "pointer" : "default",

            "& .fc-datagrid-cell-frame": {
                minHeight: "1.75rem",
            },
            "& .fc-datagrid-expander-placeholder, & .fc-icon": {
                display: "none",
            },
            "& .fc-datagrid-cell-cushion": {
                padding: ".25rem",
                paddingRight: ".75rem",
                [theme.breakpoints.down("sm")]: {
                    paddingRight: ".25rem",
                },
            },
        },
        accessLimitedRow: {
            backgroundColor: theme.palette.tailwind.slate[100],
            "& .fc-timeline-lane-frame": {
                backgroundColor: theme.palette.tailwind.slate[100],
            },
        },
        laneSkeleton: {
            "& .fc-timeline-lane-frame": {
                position: "relative",
                backgroundColor: alpha(theme.palette.text.primary, 0.1),
                borderTop: `.125rem solid ${theme.palette.common.white}`,
                borderBottom: `.125rem solid ${theme.palette.common.white}`,
                borderRadius: ".25rem",
                pointerEvents: "none",

                "& .fc-timeline-bg, & .fc-timeline-lane-misc": {
                    position: "relative",
                    zIndex: 0,
                },
                "& .fc-timeline-events": {
                    position: "relative",
                    zIndex: 2,
                },
                "&::after": {
                    content: '""',
                    position: "absolute",
                    zIndex: 1,
                    left: "0",
                    right: "0",
                    top: ".125rem",
                    bottom: ".125rem",
                    borderRadius: ".25rem",
                    background: `linear-gradient(90deg, transparent, ${theme.palette.action.hover}, transparent)`,
                    transform: "translateX(-100%)",
                    animation: `${laneSkeletonWaveKeyframes} 1.6s linear 0.5s infinite`,
                    pointerEvents: "none",
                },
            },
        },
        resourceGroupLabel: {
            "& .fc-datagrid-cell-cushion": {
                display: "flex",
                alignItems: "center",
            },
            "& .fc-datagrid-cell-main": {
                width: "100%",
            },
            "&.fc-resource-group": {
                fontWeight: "bold",
            },
        },
    }),
)
